import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Section from '../../components/ui/Section';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPlus, FiMinus } = FiIcons;

const faqs = [
  {
    q: "Für wen sind Deine Programme geeignet?",
    a: "Meine Programme richten sich an ambitionierte Frauen, die bereit sind, ihr volles Potenzial zu entfalten. Egal ob Unternehmerin, Führungskraft oder spirituelle Suchende – wenn Du spürst, dass Du für mehr bestimmt bist und bereit bist, alte Muster loszulassen, bist Du hier genau richtig."
  },
  {
    q: "Wie lange dauert ein Programm?",
    a: "Alle High-End Programme laufen über 6 Monate. Diese Zeitspanne ist bewusst gewählt – echte, nachhaltige Transformation braucht Tiefe und Zeit. Innerhalb dieser Monate arbeiten wir intensiv an Deiner inneren Haltung, Deinen Mustern und Deiner neuen Identität."
  },
  {
    q: "Was unterscheidet Dich von anderen Coaches?",
    a: "Ich verbinde spirituelle Intelligenz mit unternehmerischem Know-how. Mein Ansatz geht tiefer als klassisches Business-Coaching – wir arbeiten auf energetischer, emotionaler und strategischer Ebene gleichzeitig. Das Ergebnis: nachhaltige Transformation statt kurzfristiger Motivation."
  },
  {
    q: "Wie läuft der GlücksCall ab?",
    a: "Der GlücksCall ist ein kostenloses, 30-minütiges Erstgespräch via Zoom. Wir schauen gemeinsam, wo Du gerade stehst, was Dich blockiert und welches Programm am besten zu Deiner aktuellen Lebensphase passt. Kein Druck, kein Verkaufsgespräch – nur ehrliche Verbindung."
  },
  {
    q: "Gibt es eine Ratenzahlung?",
    a: "Ja, selbstverständlich. Alle Programme können in bequemen Monatsraten bezahlt werden. Die genauen Konditionen besprechen wir im GlücksCall individuell und transparent. Investment in Dich ist die wertvollste Entscheidung, die Du treffen kannst."
  },
  {
    q: "Was ist, wenn das Programm nicht das Richtige für mich ist?",
    a: "Deshalb gibt es den GlücksCall – damit wir gemeinsam sicherstellen, dass die Energie und das Programm wirklich zu Dir passen. Ich nehme nur Klientinnen auf, bei denen ich überzeugt bin, dass wir gemeinsam Großes erschaffen können. Deine Transformation ist mein Commitment."
  }
];

const FAQItem = ({ faq, index, isOpen, onToggle }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.08 }}
    className="border-b border-white/10 last:border-0"
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between py-7 text-left group"
    >
      <span className={`text-lg font-serif transition-colors duration-300 pr-8 ${isOpen ? 'text-gold' : 'text-cream group-hover:text-gold'}`}>
        {faq.q}
      </span>
      <span className={`shrink-0 w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-300 ${isOpen ? 'border-gold bg-gold text-dark' : 'border-white/20 text-cream/50 group-hover:border-gold group-hover:text-gold'}`}>
        {isOpen ? <FiMinus size={14} /> : <FiPlus size={14} />}
      </span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="content"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          <p className="text-cream/65 font-light leading-relaxed pb-7 text-base">
            {faq.a}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <Section className="bg-dark border-t border-white/5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
        {/* Left: Header */}
        <div className="md:sticky md:top-32">
          <p className="text-gold tracking-[0.2em] uppercase text-sm mb-4 font-semibold">FAQ</p>
          <h2 className="text-4xl md:text-5xl font-serif mb-6 leading-tight">
            Deine Fragen, <br />
            <span className="text-gradient-gold italic">meine Antworten.</span>
          </h2>
          <p className="text-cream/60 font-light text-lg leading-relaxed mb-10">
            Noch unsicher? Hier findest Du Antworten auf die häufigsten Fragen. 
            Dein GlücksCall wartet auf Dich.
          </p>
          <div className="p-6 glass-panel border border-gold/20">
            <SafeIcon name="MessageCircle" className="text-gold text-2xl mb-4" />
            <p className="text-cream font-serif text-lg mb-2">Noch offene Fragen?</p>
            <p className="text-cream/60 text-sm mb-4">Schreib mir direkt – ich antworte persönlich.</p>
            <a
              href="mailto:hallo@coachingfemale.com"
              className="text-sm uppercase tracking-widest text-gold border-b border-gold/40 hover:border-gold transition-colors pb-1"
            >
              Nachricht senden →
            </a>
          </div>
        </div>

        {/* Right: Accordion */}
        <div className="glass-panel p-8 md:p-10">
          {faqs.map((faq, idx) => (
            <FAQItem
              key={idx}
              faq={faq}
              index={idx}
              isOpen={openIndex === idx}
              onToggle={() => setOpenIndex(openIndex === idx ? null : idx)}
            />
          ))}
        </div>
      </div>
    </Section>
  );
};

export default FAQ;